import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["loading", "children", "disabled", "loadingText", "inverse", "variant", "aria-live"];
import PropTypes from 'prop-types';
import React from 'react';
import styled, { css } from 'styled-components';
import { margin } from 'styled-system';
import { getOmittableProps } from '../helpers/omit';
import { radius, textStyle } from '../helpers/theme';
import Spinner from '../Spinner/Spinner';
var variantMap = {
  action: css(["", " border-color:", ";&:hover{", " border-color:", ";}"], function (p) {
    return p.theme.colorStyles.callToAction;
  }, function (p) {
    return p.theme.colors.callToAction;
  }, function (p) {
    return p.theme.colorStyles.base;
  }, function (p) {
    return p.theme.colors.base;
  }),
  standard: css(["color:", ";background-color:", ";border-color:", ";&:hover{", " border-color:", ";}"], function (p) {
    return p.theme.colors.base;
  }, function (p) {
    return p.theme.colors.white;
  }, function (p) {
    return p.theme.colors.base;
  }, function (p) {
    return p.theme.colorStyles.base;
  }, function (p) {
    return p.theme.colors.base;
  }),
  danger: css(["", " border-color:", ";&:hover{", " border-color:", ";}"], function (p) {
    return p.theme.colorStyles.error;
  }, function (p) {
    return p.theme.colors.error;
  }, function (p) {
    return p.theme.colorStyles.errorDark;
  }, function (p) {
    return p.theme.colors.errorDark;
  }),
  warning: css(["", " border-color:", ";&:hover{", " border-color:", ";}"], function (p) {
    return p.theme.colorStyles.warning;
  }, function (p) {
    return p.theme.colors.warning;
  }, function (p) {
    return p.theme.colorStyles.warningDark;
  }, function (p) {
    return p.theme.colors.warningDark;
  }),
  success: css(["", " border-color:", ";&:hover{", " border-color:", ";}"], function (p) {
    return p.theme.colorStyles.success;
  }, function (p) {
    return p.theme.colors.success;
  }, function (p) {
    return p.theme.colorStyles.successDark;
  }, function (p) {
    return p.theme.colors.successDark;
  })
};
var inverseVariantMap = {
  action: css(["", ";border-color:", ";&:hover{color:", ";background-color:", ";border-color:", ";}"], function (p) {
    return p.theme.colorStyles.callToAction;
  }, function (p) {
    return p.theme.colors.callToAction;
  }, function (p) {
    return p.theme.colors.callToAction;
  }, function (p) {
    return p.theme.colors.white;
  }, function (p) {
    return p.theme.colors.white;
  }),
  standard: css(["", ";border-color:", ";&:hover{color:", ";background-color:", ";border-color:", ";}"], function (p) {
    return p.theme.colorStyles.base;
  }, function (p) {
    return p.theme.colors.white;
  }, function (p) {
    return p.theme.colors.base;
  }, function (p) {
    return p.theme.colors.white;
  }, function (p) {
    return p.theme.colors.white;
  }),
  danger: css(["color:", ";background-color:", ";border-color:", ";&:hover{", "}"], function (p) {
    return p.theme.colors.error;
  }, function (p) {
    return p.theme.colors.white;
  }, function (p) {
    return p.theme.colors.error;
  }, function (p) {
    return p.theme.colorStyles.error;
  }),
  warning: css(["color:", ";background-color:", ";border-color:", ";&:hover{", " border-color:", ";}"], function (p) {
    return p.theme.colors.warning;
  }, function (p) {
    return p.theme.colors.white;
  }, function (p) {
    return p.theme.colors.warning;
  }, function (p) {
    return p.theme.colorStyles.warning;
  }, function (p) {
    return p.theme.colors.warning;
  }),
  success: css(["color:", ";background-color:", ";border-color:", ";&:hover{", " border-color:", ";}"], function (p) {
    return p.theme.colors.success;
  }, function (p) {
    return p.theme.colors.white;
  }, function (p) {
    return p.theme.colors.success;
  }, function (p) {
    return p.theme.colorStyles.success;
  }, function (p) {
    return p.theme.colors.success;
  })
};
var disabledCss = /*#__PURE__*/css(["", ";border-color:", ";cursor:not-allowed;"], function (p) {
  return p.theme.colorStyles.disable;
}, function (p) {
  return p.theme.colors.lightGray;
});
var borderMap = {
  thin: css(["border:1px solid;"]),
  thick: css(["border:2px solid;"])
};

var getBorder = function getBorder(size) {
  return borderMap[size];
};

var variantOrDisabled = function variantOrDisabled(props) {
  var map = props.$inverse ? inverseVariantMap : variantMap;

  if (props.disabled) {
    return disabledCss;
  } else if (props.$variant !== undefined) {
    return map[props.$variant];
  }
};

function ButtonFunc(props, ref) {
  var loading = props.loading,
      children = props.children,
      disabled = props.disabled,
      loadingText = props.loadingText,
      inverse = props.inverse,
      variant = props.variant,
      ariaLive = props['aria-live'],
      rest = _objectWithoutProperties(props, _excluded);

  var spanProps = null;

  if (loading === true) {
    spanProps = {
      style: {
        visibility: 'hidden'
      },
      'aria-hidden': true
    };
  }

  return /*#__PURE__*/React.createElement(StyledButton, Object.assign({}, rest, {
    ref: ref,
    $inverse: inverse,
    $variant: variant,
    disabled: loading || disabled,
    "aria-live": ariaLive || 'assertive'
  }), /*#__PURE__*/React.createElement("span", spanProps, children), loading && /*#__PURE__*/React.createElement(Spinner, {
    iconSize: "small",
    "aria-label": loadingText
  }));
}

var styleProps = getOmittableProps(margin);
var StyledButton = /*#__PURE__*/styled.button.withConfig({
  shouldForwardProp: function shouldForwardProp(p) {
    return !styleProps.has(p);
  }
}).withConfig({
  displayName: "StyledButton"
})(["position:relative;padding:2px 30px;outline:none;cursor:pointer;overflow:visible;box-sizing:border-box;text-decoration:none;display:inline-block;", ";", ";border-radius:", ";&::-moz-focus-inner{border:0;}&:focus{::after{content:'';display:block;position:absolute;height:calc(100% + 10px);width:calc(100% + 10px);top:-5px;left:-5px;", ";border-radius:", ";border-color:", ";box-sizing:border-box;}}svg{display:inline-block;margin-top:-4px;}", "{position:absolute;left:50%;top:50%;margin-left:-8px;margin-top:-8px;}", " ", ""], function (p) {
  return textStyle(p.theme, 'body');
}, function (p) {
  return getBorder(p.theme.border);
}, radius(20), function (p) {
  return getBorder(p.theme.border);
}, radius(20), function (p) {
  return p.theme.colors.callToAction;
}, Spinner, margin, variantOrDisabled);
var ButtonFR = /*#__PURE__*/React.forwardRef(ButtonFunc);
export var Button = ButtonFR;
ButtonFR.displayName = 'Button';
ButtonFR.propTypes = {
  variant: PropTypes.oneOf(['standard', 'action', 'danger', 'warning', 'success']),
  disabled: PropTypes.bool,
  inverse: PropTypes.bool,
  loading: PropTypes.bool,
  loadingText: PropTypes.string
};
ButtonFR.defaultProps = {
  variant: 'standard',
  type: 'button',
  loadingText: 'loading'
};
export default Button;